import React, { useCallback, useState } from 'react'
import { useTheme, TableRow, Collapse, TableCell, Typography } from '@mui/material'
import { IOutputTable } from './models'
import { useResponsiveTableContext } from './ResponsiveTableContext'

const BodyRow = ({ apiTable, className = '', children, onClick, sx = {}, noBorderBottom = false, noHover = false }) => {
  return (
    <apiTable.components.TableBodyRow
      className={className}
      onClick={onClick}
      sx={{
        ...sx,
        ...(noBorderBottom && {
          '& td': {
            borderBottom: 'none'
          }
        }),
        ...(noHover && {
          '&:hover': {
            backgroundColor: 'transparent !important'
          },
          '&:hover td': {
            backgroundColor: 'transparent !important'
          }
        })
      }}
    >
      {children}
    </apiTable.components.TableBodyRow>
  )
}

const SeparatorRow = ({ apiTable }) => {
  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: 'transparent !important'
        }
      }}
    >
      <TableCell
        colSpan={apiTable.columns.length}
        sx={{
          p: 0,
          borderBottom: '1px solid #F7F7F7',
          backgroundColor: 'transparent',
          height: '1px'
        }}
      />
    </TableRow>
  )
}

const BodyContent = ({
  apiTable,
  noItemsMessage,
  endMessage
}: {
  apiTable: IOutputTable;
  noItemsMessage?: string;
  endMessage?: React.ReactNode
}) => {
  const [activeRow, setActiveRow] = useState<string | undefined>()
  const theme = useTheme()
  const responsiveTableContext = useResponsiveTableContext()

  const rowIsOnError = useCallback((item) => {
    if (apiTable.rowIsOnError) {
      return apiTable.rowIsOnError(item)
    }
    return false
  }, [apiTable.rowIsOnError])

  const rowIsBold = useCallback((item) => {
    if (apiTable.rowIsBold) {
      return apiTable.rowIsBold(item)
    }
    return false
  }, [apiTable.rowIsBold])

  return (
    <apiTable.components.TableBody>
      {apiTable.items.map((item, index) => {
        const isExpanded = apiTable.expandedRowRender && responsiveTableContext
          ? responsiveTableContext.isRowExpanded(item[apiTable.rowKey] as string)
          : false
        const isLastItem = index === apiTable.items.length - 1
        const isBold = rowIsBold(item)

        return (
          <React.Fragment key={item[apiTable.rowKey || 'uuid'] as string}>
            <BodyRow
              apiTable={apiTable}
              className={activeRow && activeRow === item[apiTable.rowKey] ? 'active' : ''}
              noBorderBottom={isExpanded}
              noHover={isExpanded}
              onClick={apiTable.onRowClick
                ? e => {
                    setActiveRow(item[apiTable.rowKey] as string)
                    apiTable.onRowClick?.(e, item)
                  }
                : undefined}
            >
              {apiTable.columns.map(column => (
                <apiTable.components.TableBodyCell
                  key={column.name}
                  style={{ ...column.style, ...column.valueStyle }}
                  multiple={Boolean(column.multiple)}
                  sx={{
                    color: rowIsOnError(item) ? '#FF0000' : theme.palette.grey[700],
                    fontWeight: isBold ? '500 !important' : 'inherit',
                    '& *': {
                      fontWeight: isBold ? '500 !important' : 'inherit'
                    }
                  }}
                >
                  {apiTable.bodyColumnContent(column.name, item)}
                </apiTable.components.TableBodyCell>
              ))}
            </BodyRow>
            {apiTable.expandedRowRender && (
              <>
                <BodyRow
                  apiTable={apiTable}
                  noHover={true}
                  onClick={undefined}
                >
                  <TableCell
                    colSpan={apiTable.columns.length}
                    sx={{
                      p: isExpanded ? 1.5 : 0,
                      border: 'none',
                      backgroundColor: 'transparent'
                    }}
                  >
                    <Collapse
                      in={isExpanded}
                      timeout="auto"
                      unmountOnExit
                    >
                      {apiTable.expandedRowRender(item, {
                        t: () => '',
                        items: apiTable.items
                      })}
                    </Collapse>
                  </TableCell>
                </BodyRow>
                {isExpanded && !isLastItem && <SeparatorRow apiTable={apiTable} />}
              </>
            )}
          </React.Fragment>
        )
      })}
      {Boolean(noItemsMessage) && apiTable.items.length === 0 && (
        <TableRow>
          <apiTable.components.TableBodyCell colSpan={apiTable.columns.length}>
            {noItemsMessage}
          </apiTable.components.TableBodyCell>
        </TableRow>
      )}
      {apiTable.loadingNext && <apiTable.components.TableRowLoading colSpan={apiTable.columns.length} />}
      {Boolean(endMessage) && apiTable.items.length > 0 && !apiTable.loadingNext && (
        <TableRow>
          <TableCell colSpan={apiTable.columns.length} align="center" sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {endMessage}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </apiTable.components.TableBody>
  )
}

type Sort = {
  by: string,
  direction: string,
  onChange: (sort: string) => void
}

const TableViewBase = ({
  sort,
  apiTable,
  noItemsMessage,
  endMessage
}: {
  sort?: Sort,
  noItemsMessage?: string,
  endMessage?: React.ReactNode,
  view?: string,
  apiTable: IOutputTable
}) => {
  return (
    <apiTable.components.Table stickyHeader>
      <apiTable.components.TableHead>
        <apiTable.components.TableHeadRow>
          {apiTable.columns.map(column => {
            const cellProps = column.header.cellProps ? column.header.cellProps() : {}
            return column.sortable ? (
              <apiTable.components.TableHeaderSortableCell
                key={column.name}
                column={column}
                sort={sort}
                style={column.style}
                {...cellProps}
              >
                {apiTable.headerColumnContent(column.name)}
              </apiTable.components.TableHeaderSortableCell>
            ) : (
              <apiTable.components.TableHeaderCell
                key={column.name}
                style={column.style}
                {...cellProps}
              >
                {apiTable.headerColumnContent(column.name)}
              </apiTable.components.TableHeaderCell>
            )
          })}
        </apiTable.components.TableHeadRow>
      </apiTable.components.TableHead>
      {apiTable.loading ? (
        <apiTable.components.TableBody>
          <apiTable.components.TableRowLoading colSpan={apiTable.columns.length} />
        </apiTable.components.TableBody>
      ) : (
        <BodyContent
          apiTable={apiTable}
          noItemsMessage={noItemsMessage}
          endMessage={endMessage}
        />
      )}
    </apiTable.components.Table>
  )
}

export default TableViewBase
